import { from as observableFrom, Subject, Subscription, Observable, Observer, interval } from 'rxjs';

import { mergeMap, map } from 'rxjs/operators';
import { Publication } from './../interfaces/publication';
import { PublicationService } from './publication.service';
import { SapiService } from '../services/sapi.service';
import { Injectable } from '@angular/core';

export class Listener {
	count = 0;
	observable: Subject<any> = new Subject<any>();
}

export enum SocketState {
	open = 1,
	closed = 2
}

@Injectable()
export class WebSocketsService {
	public bbNotificationChannels: Subject<string> = new Subject<string>();
	public websocketStateObservable: Subject<{state: SocketState}>;

	private socket: Subject<MessageEvent>;

	private sessionId: string;
	private uuid: string;
	private webSocket: WebSocket;
	private messagesToWebsocket: any[] = [];
	private heartbeatSubscription: Subscription;
	private baseUrl = '';
	private reconnect = false;
	private processMessageSubscription: Subscription;
	private subscriptions: string[] = [];
	private channelSubscriptions: Map<string, Listener> = new Map<string, Listener>();

	private permissionGrantedFromBackend = true;

	private _useDevelopment: boolean;

	private readonly RECONNECT_IN_MILLISECONDS: number = 60000;

	constructor(private publicationService: PublicationService, private sapiService: SapiService) {
		this.publicationService.getData().subscribe((publication: Publication) => {
			if (publication) {
				this.baseUrl = publication.baseurl;
				this.processMessageSubscription = this.processMessages().subscribe(() => { });
			}
		});
		this.websocketStateObservable = new Subject<{state: SocketState}>();
		this._useDevelopment = false;
	}

	get useDevelopment(): boolean {
		return this._useDevelopment;
	}

	set useDevelopment(development: boolean) {
		if (this._useDevelopment !== development) {
			this._useDevelopment = development;
			this.reconnectSocket();
		}
	}

	public unsubscribeFromChannel(channel: string) {
		if (this.channelSubscriptions[channel]) {
			if (this.channelSubscriptions[channel].count > 0) {
				this.channelSubscriptions[channel].count--;
			}
			if (this.channelSubscriptions[channel].count === 0) {
				this.sendMessage({ unsubscribe: channel });
				this.subscriptions = this.subscriptions.filter(c => c !== channel);
			}
		}
	}

	public subscribeToChannel(channel: string): Observable<any> {
		if (!this.channelSubscriptions[channel] || this.channelSubscriptions[channel].count === 0) {
			this.sendMessage({ subscribe: channel });
			this.subscriptions.push(channel);

			const listener: Listener = new Listener();

			this.channelSubscriptions[channel] = listener;
		}

		this.channelSubscriptions[channel].count++;
		return this.channelSubscriptions[channel].observable;
	}

	public addBbNotification(channelName: string) {
		this.bbNotificationChannels.next(channelName);
	}

	public processMessages(): Observable<any> {
		return this.getSessionId().pipe(mergeMap(() => this.connect().pipe(
			map((messageEvent: any) => {
				if (this.messagesToWebsocket.length > 0) {
					this.delayedProcessing();
				}

				let object: any;

				try {
					object = JSON.parse(messageEvent.data);
				} catch (e) {
					// What should we do with this error message?
					return;
				}

				let data: any = null;
				let channel: string = null;

				if ('channel' in object && 'message' in object) {
					channel = object.channel;
					data = this.parseJson(object.message);
				}

				if (channel in this.channelSubscriptions) {
					if (this.channelSubscriptions[channel].count === 0) {
						this.sendMessage({ unsubscribe: channel });
					}
					this.channelSubscriptions[channel].observable.next(data);
				}

				return data;
			})
		)));
	}

	public connect(): Subject<MessageEvent> {
		if (!this.socket) {
			let url: string;

			this.uuid = this.uuidv4();

			if (this.baseUrl.search(/\.dev\./) > -1 || this.baseUrl.search(/^dev\./) > -1) {
				if (this.useDevelopment) {
					url = 'wss://dev-ws.test.bbvms.com/wss';
				} else {
					url = 'wss://ws.bbvms.com/wss';
				}
			} else if (this.baseUrl.search(/\.acc\./) > -1) {
				url = 'wss://acc-ws.test.bbvms.com/wss';
			} else if (this.baseUrl.search(/\.test\./) > -1) {
				url = 'wss://ws.test.bbvms.com/wss';
			} else {
				url = 'wss://ws.bbvms.com/wss';
			}

			url += '?sess=' + this.sessionId + '&uuid=' + this.uuid;

			this.socket = this.create(url);
		}

		return this.socket;
	}

	reconnectSocket() {
		this.reconnect = true;

		if (this.processMessageSubscription) {
			this.processMessageSubscription.unsubscribe();
		}

		this.webSocket = null;
		this.socket = null;

		this.processMessageSubscription = this.processMessages().subscribe(() => {
			if (this.reconnect) {
				for (const channel of this.subscriptions) {
					this.sendMessage({ subscribe: channel });
				}
				this.reconnect = false;
				this.websocketStateObservable.next({ state: SocketState.open });
			}
		});
	}

	async getPublication() {
		return await this.publicationService.getData().toPromise();
	}

	private sendMessage(message: any) {
		message.sessionId = this.sessionId;
		message.uuid = this.uuid;

		if (this.webSocket) {
			if (this.webSocket.readyState === WebSocket.OPEN) {
				this.webSocket.send(JSON.stringify(message));
			} else {
				if (this.messagesToWebsocket.indexOf(message) === -1) {
					this.messagesToWebsocket.push(message);
				}
			}
		} else {
			if (this.messagesToWebsocket.indexOf(message) === -1) {
				this.messagesToWebsocket.push(message);
			}
		}
	}

	private delayedProcessing() {
		if (this.messagesToWebsocket.length > 0) {
			const messages: any[] = [];
			for (const message of this.messagesToWebsocket) {
				message.sessionId = this.sessionId;
				if (this.webSocket) {
					if (this.webSocket.readyState === WebSocket.OPEN) {
						this.sendMessage(message);
					} else {
						messages.push(message);
						this.reconnect = true;
					}
				}
			}
			this.messagesToWebsocket = messages;
		}
	}

	private getSessionId(): Observable<string> {
		if (this.sessionId) {
			return observableFrom(this.sessionId);
		} else {
			return this.sapiService.get('auth', 'getSessionId').pipe(
				map((response: any) => {
					if (response && response.body) {
						this.sessionId = response.body;
					}

					return this.sessionId;
				})
			);
		}
	}

	private parseJson(data: any): any {
		if (typeof data === 'string') {
			try {
				data = JSON.parse(data);
			} catch (e) {
				// What should we do with this error message?
				return data;
			}
		}
		return data;
	}


	// Not unique enough for crypto, but unique enough for multiple sessions in 1 browser
	private uuidv4() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = Math.random() * 16 | 0; const v = c === 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}

	private create(url: string): Subject<MessageEvent> {
		while (!this.webSocket) {
			try {
				this.webSocket = new WebSocket(url);
			} catch (e) {

			}
		}

		const observable = new Observable(
			(localObserver: Observer<MessageEvent>) => {
				this.webSocket.onmessage = localObserver.next.bind(localObserver);
				this.webSocket.onerror = localObserver.error.bind(localObserver);
				this.webSocket.onclose = localObserver.complete.bind(localObserver);

				return this.webSocket.close.bind(this.webSocket);
			}
		);

		const observer = {
			next: (data: any) => {
				if (this.webSocket.readyState === WebSocket.OPEN) {
					this.webSocket.send(JSON.stringify(data));
				}
			},
			error: () => { },
			complete: () => { }
		};

		this.subscribeToChannel('default').subscribe(message => {
			if (message !== 'ok') {
				this.permissionGrantedFromBackend = false;
				if (this.heartbeatSubscription) {
					this.heartbeatSubscription.unsubscribe();
				}
			}
		});

		if (this.heartbeatSubscription) {
			this.heartbeatSubscription.unsubscribe();
		}
		if (this.permissionGrantedFromBackend) {
			this.heartbeatSubscription = interval(this.RECONNECT_IN_MILLISECONDS).subscribe(
				() => {
					if (!this.webSocket || this.webSocket.readyState === WebSocket.CLOSED ||
						this.webSocket.readyState === WebSocket.CLOSING) {
						this.reconnectSocket();
					}
				}
			);
		}

		return Subject.create(observer, observable);
	}
}
